import React, { useEffect, useRef, useState } from 'react';
import { setApiToken } from 'src/api';
import { useLocalStorage } from 'src/ui-kit/useLocalStorage';
import { extractAdminFromToken } from './authService';
import { isPublicAuthPath } from './authPaths';
import { clearAuthLocalStorage } from './authStorage';
import { getAdminTokenExpiresAt, isAdminTokenValid } from './tokenExpiry';

import { getEntityPolicyMap } from './policies';
import { msalInstance } from '../azure-ad/msal';
import { useIsAuthenticated } from '@azure/msal-react';
import { Spinner } from 'reactstrap';

export { clearAuthLocalStorage } from './authStorage';

export const AuthContext = React.createContext({
  token: null,
  setToken(token) {
    void token;
  },
  state: null,
  admin: null,
  getEntityPolicy: () => ({}),
});

export const AuthProvider = ({ initialState, children }) => {
  const [token, _setToken] = useLocalStorage('token');
  const [state, setState] = useLocalStorage('auth', initialState);
  const [featureFlags, _setFeatureFlags] = useLocalStorage('featureFlags');
  const policyMap = getEntityPolicyMap(state && state.role);
  const isADAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const isRedirecting = useRef(false);

  function setToken(token) {
    if (!token) {
      _setToken(null);
      setApiToken(undefined);
      setState(null);
      return;
    }
    try {
      const admin = extractAdminFromToken(token);
      _setToken(token);
      setApiToken(token);
      setState(admin);
    } catch (err) {
      _setToken(null);
      setApiToken(undefined);
      setState(null);
    }
  }

  async function signout() {
    setToken(null);
    if (isADAuthenticated) {
      await msalInstance.logoutRedirect();
    }
  }

  function setFeatureFlags(featureFlagMaps) {
    if (!token) {
      _setFeatureFlags(null);
      return;
    }
    _setFeatureFlags(featureFlagMaps);
  }

  useEffect(() => {
    let expiryTimer;

    const clearExpiryTimer = () => {
      if (expiryTimer !== undefined) {
        window.clearTimeout(expiryTimer);
        expiryTimer = undefined;
      }
    };

    const expireSession = () => {
      clearExpiryTimer();

      const pathname = window.location.pathname;
      const isPublicPath = isPublicAuthPath(pathname);

      if (!isPublicPath) setIsLoading(true);

      clearAuthLocalStorage();
      setApiToken();
      _setToken(null);
      setState(null);

      if (isPublicPath) {
        isRedirecting.current = false;
        setIsLoading(false);
        return;
      }

      if (!isRedirecting.current) {
        isRedirecting.current = true;
        window.location.replace('/login');
      }
    };

    const scheduleExpiryValidation = () => {
      clearExpiryTimer();

      const expiresAt = getAdminTokenExpiresAt(token);
      if (expiresAt === undefined) return;

      const delay = Math.max(0, expiresAt - Date.now() + 100);
      const safeDelay = Math.min(delay, 2_147_000_000);
      expiryTimer = window.setTimeout(validateSession, safeDelay);
    };

    const validateSession = () => {
      if (!isAdminTokenValid(token)) {
        expireSession();
        return;
      }

      let currentAdmin;
      try {
        currentAdmin = extractAdminFromToken(token);
      } catch (error) {
        expireSession();
        return;
      }

      setApiToken(token);
      isRedirecting.current = false;
      setIsLoading(false);

      // token and decoded admin are persisted by two separate state setters.
      // Rebuild a missing/stale admin from the valid JWT instead of treating
      // that short-lived transition as an expired session.
      if (
        !state ||
        state.id !== currentAdmin.id ||
        state.exp !== currentAdmin.exp
      ) {
        setState(currentAdmin);
      }

      scheduleExpiryValidation();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') validateSession();
    };

    const handleWindowActive = () => validateSession();
    const handleStorage = (event) => {
      if (event.key === 'token' || event.key === 'auth' || event.key === null) {
        const storedToken = window.localStorage.getItem('token');
        let parsedToken;

        try {
          parsedToken = storedToken ? JSON.parse(storedToken) : null;
        } catch (error) {
          expireSession();
          return;
        }

        if (!parsedToken || parsedToken !== token) {
          expireSession();
          return;
        }

        validateSession();
      }
    };

    validateSession();
    window.addEventListener('focus', handleWindowActive);
    window.addEventListener('pageshow', handleWindowActive);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearExpiryTimer();
      window.removeEventListener('focus', handleWindowActive);
      window.removeEventListener('pageshow', handleWindowActive);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state, token]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        state, // TODO: Rename state to admin?
        admin: state,
        getEntityPolicy: (entityName) =>
          policyMap[entityName] || policyMap.default,
        featureFlags,
        setFeatureFlags,
        signout,
      }}
    >
      {isLoading ? <Spinner /> : children}
    </AuthContext.Provider>
  );
};

export const AuthConsumerHOC = (Component) =>
  class AuthConsumer extends React.Component {
    render() {
      return (
        <AuthContext.Consumer>
          {(auth) => <Component {...this.props} auth={auth} />}
        </AuthContext.Consumer>
      );
    }
  };
